import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';

export default function EmailVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { completeAuthentication } = useAuth();
  const initialChallenge = searchParams.get('challengeId') || location.state?.challengeId || '';
  const initialEmail = location.state?.email || '';

  const [challengeId, setChallengeId] = useState(initialChallenge);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [expiresAt, setExpiresAt] = useState(location.state?.expiresAt || null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = window.setInterval(() => setResendCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const maskedEmail = useMemo(() => {
    if (!email || !email.includes('@')) return email || 'your email address';
    const [name, domain] = email.split('@');
    const prefix = name.length <= 2 ? name[0] : name.slice(0, 2);
    return prefix + '•••@' + domain;
  }, [email]);

  const handleVerify = async (event) => {
    event.preventDefault();
    setServerError('');
    if (!/^[0-9]{6}$/.test(code)) {
      setServerError('Enter the 6-digit verification code.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.auth.verifyEmail({ challengeId, code });
      const data = response.data?.data || {};
      await completeAuthentication(data);
      if (data.securityQuestionsRequired) {
        navigate('/security-questions?mode=required', { replace: true, state: { required: true } });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.code === 'VERIFICATION_CODE_EXPIRED') {
        setServerError('Your verification code has expired. Please request a new code.');
      } else if (payload.code === 'VERIFICATION_ATTEMPTS_EXCEEDED') {
        setServerError('Too many incorrect attempts. Please request a new code.');
      } else {
        setServerError(payload.message || 'The verification code is incorrect. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!challengeId || resendCooldown > 0 || resending) return;
    setServerError('');
    setResending(true);
    try {
      const response = await api.auth.resendVerificationCode({ challengeId });
      const data = response.data?.data || {};
      setChallengeId(data.challengeId || challengeId);
      setExpiresAt(data.expiresAt || null);
      setCode('');
      setResendCooldown(60);
      navigate('/verify-email?challengeId=' + encodeURIComponent(data.challengeId || challengeId), {
        replace: true,
        state: { email: data.email || email, expiresAt: data.expiresAt || null },
      });
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.retryAfterSeconds) setResendCooldown(Number(payload.retryAfterSeconds));
      setServerError(payload.message || 'Unable to resend the verification code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!challengeId) {
    return (
      <div style={{ maxWidth: 440, margin: '0 auto', padding: '60px 20px', textAlign: 'center', color: '#fff' }}>
        <h1 style={{ fontSize: '1.7rem' }}>Email verification</h1>
        <p style={{ color: '#888', lineHeight: 1.6 }}>This verification session is missing or invalid. Please start registration again.</p>
        <button onClick={() => navigate('/register')} style={{ marginTop: 18, padding: '12px 18px', border: 0, borderRadius: 10, background: '#00d4aa', fontWeight: 700 }}>Back to registration</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 440, margin: '0 auto', padding: '60px 20px', color: '#fff' }}>
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ fontSize: 26, fontWeight: 900, marginBottom: 20 }}>9ja<span style={{ color: '#00d4aa' }}>Win</span></div>
        <h1 style={{ fontSize: '1.75rem', marginBottom: 8 }}>Confirm your email</h1>
        <p style={{ color: '#888', lineHeight: 1.6, margin: 0 }}>
          We sent a 6-digit verification code to <strong style={{ color: '#fff' }}>{maskedEmail}</strong>.
        </p>
      </div>

      {serverError && (
        <div style={{ background: 'rgba(255,77,77,.1)', border: '1px solid #ff4d4d', color: '#ff4d4d', padding: '12px 16px', borderRadius: 10, marginBottom: 18, fontSize: 14 }}>
          ⚠️ {serverError}
        </div>
      )}

      <form onSubmit={handleVerify}>
        <label style={{ display: 'block', marginBottom: 8, color: '#aaa', fontSize: 13, fontWeight: 700, letterSpacing: '.5px' }}>
          VERIFICATION CODE
        </label>
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          placeholder="000000"
          style={{ width:'100%', boxSizing:'border-box', padding:'16px', borderRadius:10, border:'1px solid #2a2a2a', background:'#1a1a1a', color:'#fff', fontSize:24, letterSpacing:8, textAlign:'center', outline:'none' }}
        />
        <p style={{ color:'#666', fontSize:12, textAlign:'center', margin:'10px 0 22px' }}>
          The code expires in 5 minutes.
          {expiresAt ? ' Enter it before it expires.' : ''}
        </p>
        <button type="submit" disabled={loading || code.length !== 6} style={{ width:'100%', padding:14, border:0, borderRadius:10, background:'#00d4aa', color:'#000', fontWeight:800, fontSize:16, opacity:loading || code.length !== 6 ? .55 : 1 }}>
          {loading ? 'Verifying…' : 'Verify Email'}
        </button>
      </form>

      <button type="button" onClick={handleResend} disabled={resending || resendCooldown > 0} style={{ display:'block', margin:'20px auto 0', border:0, background:'transparent', color:'#00d4aa', fontWeight:700, cursor:resending || resendCooldown > 0 ? 'not-allowed' : 'pointer', opacity:resending || resendCooldown > 0 ? .55 : 1 }}>
        {resending ? 'Sending…' : resendCooldown > 0 ? 'Resend code in ' + resendCooldown + 's' : 'Resend code'}
      </button>
    </div>
  );
}
